import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Stack,
  Avatar,
  Card,
  Tooltip,
  CardContent,
  Skeleton
} from '@mui/material';
import { PlayCircleOutline, DeleteOutline } from '@mui/icons-material';
import palette from 'src/theme/palette';
import PhotoCamera from '@mui/icons-material/PhotoCamera';

const ImageShimmerLoader = () => (
  <Skeleton
    variant="rectangular"
    width="100%"
    height={140}
    sx={{ borderRadius: 1 }}
    animation="wave"
  />
);

const VideosTab = ({
  form,
  uploadingVideos,
  handleVideoSelect,
  removeVideo,
  handleVideoThumbnailUpload,
  removeVideoThumbnail,
  uploadingVideoThumbnails,
  loadingProperty,
  isAnythingLoading
}) => {
  const isLoading = isAnythingLoading ? isAnythingLoading() : loadingProperty;

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Property Video Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1">Property Video</Typography>
          <input
            accept="video/*"
            id="property-video"
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => handleVideoSelect(e, 'property')}
            disabled={isLoading}
          />
          <label htmlFor="property-video">
            <Button
              variant="contained"
              component="span"
              startIcon={<PlayCircleOutline />}
              sx={{ mt: 1 }}
              disabled={uploadingVideos?.property || isLoading}
            >
              {uploadingVideos?.property ? 'Uploading...' : 'Select Video'}
            </Button>
          </label>

          {uploadingVideos?.property && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="text" width={200} height={20} />
              </Stack>
            </Paper>
          )}

          {form.propertyVideo && !uploadingVideos?.property && (
            <Paper variant="outlined" sx={{ mt: 2, p: 1, cursor: 'pointer' }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <a
                    href={form.propertyVideo.preview || form.propertyVideo.serverUrl}
                    data-fancybox="property-videos"
                    data-caption={form.propertyVideo.name}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <PlayCircleOutline />
                    </Avatar>
                  </a>
                  <Typography variant="body2">{form.propertyVideo.name}</Typography>
                </Stack>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    removeVideo('property');
                  }}
                  disabled={isLoading}
                >
                  <DeleteOutline />
                </IconButton>
              </Stack>
            </Paper>
          )}
        </Grid>

        {/* Drone Video Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1">Drone Video</Typography>
          <input
            accept="video/*"
            id="drone-video"
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => handleVideoSelect(e, 'drone')}
            disabled={isLoading}
          />
          <label htmlFor="drone-video">
            <Button
              variant="contained"
              component="span"
              startIcon={<PlayCircleOutline />}
              sx={{ mt: 1 }}
              disabled={uploadingVideos?.drone || isLoading}
            >
              {uploadingVideos?.drone ? 'Uploading...' : 'Select Drone Video'}
            </Button>
          </label>

          {uploadingVideos?.drone && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="text" width={200} height={20} />
              </Stack>
            </Paper>
          )}

          {form.droneVideo && !uploadingVideos?.drone && (
            <Paper variant="outlined" sx={{ mt: 2, p: 1, cursor: 'pointer' }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <a
                    href={form.droneVideo.preview || form.droneVideo.serverUrl}
                    data-fancybox="property-videos"
                    data-caption={form.droneVideo.name}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <PlayCircleOutline />
                    </Avatar>
                  </a>
                  <Typography variant="body2">{form.droneVideo.name}</Typography>
                </Stack>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    removeVideo('drone');
                  }}
                  disabled={isLoading}
                >
                  <DeleteOutline />
                </IconButton>
              </Stack>
            </Paper>
          )}
        </Grid>

        {/* Thumbnails Section */}
        <Grid container spacing={2} mt={2} p={2} cursor="pointer">
          {/* Property Video Thumbnail */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Property Video Thumbnail *
            </Typography>

            <Button
              component="label"
              variant="contained"
              startIcon={<PhotoCamera />}
              disabled={isLoading || !form.propertyVideo}
              sx={{
                background: palette.primary.main,
                '&:hover': { background: palette.primary.dark }
              }}
            >
              Upload Thumbnail
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleVideoThumbnailUpload(e, 'property')}
                disabled={isLoading || !form.propertyVideo}
              />
            </Button>

            {!form.propertyVideo && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                Please upload a property video first
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              {/* Uploading thumbnails */}
              {uploadingVideoThumbnails?.property?.map((thumb, i) => (
                <Box key={`uploading-property-thumb-${i}`} sx={{ mb: 2 }}>
                  <ImageShimmerLoader />
                </Box>
              ))}

              {/* Uploaded thumbnail */}
              {form.propertyVideoThumbnail &&
                (!uploadingVideoThumbnails?.property ||
                  uploadingVideoThumbnails.property.length === 0) && (
                  <Card sx={{ position: 'relative', height: '100%' }}>
                    {form.propertyVideoThumbnail.uploadError && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bgcolor: 'error.main',
                          color: 'white',
                          p: 0.5,
                          textAlign: 'center',
                          zIndex: 1
                        }}
                      >
                        <Typography variant="caption">Upload Failed</Typography>
                      </Box>
                    )}
                    <Avatar
                      variant="rounded"
                      src={
                        form.propertyVideoThumbnail.preview || form.propertyVideoThumbnail.serverUrl
                      }
                      sx={{ width: '100%', height: 140, borderRadius: 1 }}
                    />
                    <CardContent sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap>
                        {form.propertyVideoThumbnail.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => removeVideoThumbnail('property')}
                            disabled={isLoading}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

              {/* Empty state */}
              {!form.propertyVideoThumbnail &&
                (!uploadingVideoThumbnails?.property ||
                  uploadingVideoThumbnails.property.length === 0) &&
                form.propertyVideo && (
                  <Typography variant="body2" color="text.secondary" align="center">
                    No thumbnail uploaded yet.
                  </Typography>
                )}
            </Box>
          </Grid>

          {/* Drone Video Thumbnail */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Drone Video Thumbnail *
            </Typography>

            <Button
              component="label"
              variant="contained"
              startIcon={<PhotoCamera />}
              disabled={isLoading || !form.droneVideo}
              sx={{
                background: palette.primary.main,
                '&:hover': { background: palette.primary.dark }
              }}
            >
              Upload Thumbnail
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleVideoThumbnailUpload(e, 'drone')}
                disabled={isLoading || !form.droneVideo}
              />
            </Button>

            {!form.droneVideo && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                Please upload a drone video first
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              {/* Uploading thumbnails */}
              {uploadingVideoThumbnails?.drone?.map((thumb, i) => (
                <Box key={`uploading-drone-thumb-${i}`} sx={{ mb: 2 }}>
                  <ImageShimmerLoader />
                </Box>
              ))}

              {/* Uploaded thumbnail */}
              {form.droneVideoThumbnail &&
                (!uploadingVideoThumbnails?.drone ||
                  uploadingVideoThumbnails.drone.length === 0) && (
                  <Card sx={{ position: 'relative', height: '100%' }}>
                    {form.droneVideoThumbnail.uploadError && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bgcolor: 'error.main',
                          color: 'white',
                          p: 0.5,
                          textAlign: 'center',
                          zIndex: 1
                        }}
                      >
                        <Typography variant="caption">Upload Failed</Typography>
                      </Box>
                    )}
                    <Avatar
                      variant="rounded"
                      src={form.droneVideoThumbnail.preview || form.droneVideoThumbnail.serverUrl}
                      sx={{ width: '100%', height: 140, borderRadius: 1 }}
                    />
                    <CardContent sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap>
                        {form.droneVideoThumbnail.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => removeVideoThumbnail('drone')}
                            disabled={isLoading}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

              {/* Empty state */}
              {!form.droneVideoThumbnail &&
                (!uploadingVideoThumbnails?.drone || uploadingVideoThumbnails.drone.length === 0) &&
                form.droneVideo && (
                  <Typography variant="body2" color="text.secondary" align="center">
                    No thumbnail uploaded yet.
                  </Typography>
                )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VideosTab;
